class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    // Use requestAnimationFrame to ensure DOM is painted before animation starts
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', (event) => {
      event.stopPropagation();
      if (!event.target.matches('[data-recommendation-variant]')) return;
      const form = event.target.closest('form');
      const variant = event.target;
      form.querySelector('[data-recommendation-price]').textContent = variant.dataset.price;
      form.elements.quantity.value = variant.dataset.quantityMin || 1;
      const button = form.querySelector('button[type="submit"]');
      const added = variant.dataset.inCart === 'true';
      button.disabled = added;
      button.classList.toggle('is-added', added);
      button.querySelector('span').textContent = added ? this.dataset.addedLabel : this.dataset.addLabel;
      button.setAttribute('aria-label', `${added ? this.dataset.addedLabel : this.dataset.addAriaLabel}: ${form.dataset.productTitle}`);
    });
    this.addEventListener('submit', this.addToCart.bind(this));
  }

  async addToCart(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.pending) return;

    const form = event.target;
    const drawer = this.closest('cart-drawer');
    const error = this.querySelector('[role="alert"]');
    const buttons = this.querySelectorAll('button[type="submit"]');
    const formData = new FormData(form);
    const variantId = formData.get('id');
    formData.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
    formData.append('sections_url', window.location.pathname);
    this.pending = true;
    form.setAttribute('aria-busy', 'true');
    buttons.forEach((button) => { button.disabled = true; });
    error.hidden = true;

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const cartData = await response.json();
      if (!response.ok || cartData.status) throw new Error(cartData.description || window.cartStrings.error);
      if (drawer.getSectionsToRender().some((section) => !cartData.sections?.[section.id])) {
        window.location.assign(routes.cart_url);
        return;
      }
      drawer.renderContents(cartData);
      const addedProduct = drawer.querySelector(`[data-product-id="${form.dataset.productId}"] .cart-drawer__recommendation-title`);
      trapFocus(drawer.querySelector('.drawer__inner'), addedProduct || drawer.querySelector('.drawer__close--text'));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData, variantId });
    } catch (exception) {
      error.textContent = exception.message || window.cartStrings.error;
      error.hidden = false;
    } finally {
      this.pending = false;
      form.removeAttribute('aria-busy');
      buttons.forEach((button) => { button.disabled = button.classList.contains('is-added'); });
    }
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
